import "core-js/modules/es.symbol.description.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { IconButton, LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { YesNoAnswer } from '../../generated-types';
import restClient from '../../restClient';
import { CheckboxField } from '../../types/checkbox-field';
import { DELETED_ROW_ID } from '../../types/list-question-type';
import SelectInput from '../../types/select-input';
import StandaloneYesNoQuestionType from '../../types/standalone-yes-no-question-type';
import { StandardInputField } from '../../types/standard-input-field';
import BeneficialOwnersHelpModal from '../../util/helpModals/beneficial-owners-help-modal';
import { composeValidators, isValidSSNFactory, maxValueOfNumber, optional, positiveNumbersOnly, requiredField, requiresAtLeastOneBeneficialOwnerCheckboxSelected, valueNotAllowed } from '../../util/validator';
import ErrorMessage from '../error-message';
const CorporateBeneficialOwnerActorItem = _ref => {
  var _countries$countries, _values35, _values36, _values37, _values38, _values39, _values40, _values41, _values42, _values43, _values44, _values45, _values46, _values47, _values48, _values49;
  let {
    prefix,
    countries,
    pepRelationTypes,
    pepAppointmentTypes,
    reasons,
    isFromServer,
    onRemoveRow,
    values,
    setValueOfField
  } = _ref;
  const labels = {
    isPep: 'Er, eller har reell rettighetshaver vært, en politisk eksponert person (PEP)? Dette gjelder også reell rettighetshavers familiemedlemmer og kjente medarbeidere. En politisk eksponert person er en person som innehar, eller har innehatt en høytstående stilling eller verv. (Gjelder ikke på kommune- og fylkesnivå.) ',
    isRca: 'Er, eller har reell rettighetshaver vært, et nært familiemedlem eller en kjent medarbeider til en politisk eksponert person (RCA)?',
    pepCountryCode: 'PEP land',
    rcaCountryCode: 'RCA land',
    pepRelationType: 'PEP stilling',
    rcaRelationType: 'Rettighetshavers relasjon til PEP',
    pepAppointmentType: 'Dersom reell rettighetshaver har bestemmende kontroll, marker hvilke typer',
    hasAmericanTaxRelation: 'Er reell rettighetshaver en amerikansk juridisk person eller sammenslutning, eller er skattemessig hjemmehørende i USA?',
    tin: 'Tax Identification Number (TIN)',
    reasonForMissingTaxNumber: 'Dersom reell rettighetshaver ikke har TIN, oppgi årsak',
    hasInternationalTaxRelations: 'Er reell rettighetshaver skattemessig hjemmehørende i andre land?',
    hasMoreBeneficialOwners: 'Finnes det andre reelle rettighetshavere?'
  };
  const actor = values["_".concat(prefix)];
  const [internationalTaxRelationRows, setInternationalTaxRelationRows] = useState([]);
  const [lastUsedIndex, setLastUsedIndex] = useState(0);
  const [pepRows, setPepRows] = useState([]);
  const [lastUsedPepIndex, setLastUsedPepIndex] = useState(0);
  const [rcaRows, setRcaRows] = useState([]);
  const [lastUsedRcaIndex, setLastUsedRcaIndex] = useState(0);
  const addRow = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedIndex + 1;
    setLastUsedIndex(newIndex);
    const newRow = {
      id,
      index: newIndex
    };
    setInternationalTaxRelationRows([...internationalTaxRelationRows, newRow]);
  }, [setInternationalTaxRelationRows, lastUsedIndex, internationalTaxRelationRows]);
  const onRemove = (id, setValueOfField) => {
    const indexToRemove = internationalTaxRelationRows.findIndex(row => id === row);
    if (indexToRemove >= 0) {
      const newRows = [...internationalTaxRelationRows];
      newRows[indexToRemove].id = DELETED_ROW_ID;
      setInternationalTaxRelationRows(newRows);
      if (setValueOfField) {
        setValueOfField({
          key: "_".concat(prefix, ".taxRelations._").concat(indexToRemove),
          value: undefined
        });
      }
    }
  };
  const [postalCodeCheck, setPostalCodeCheck] = useState(undefined);
  const checkPostalCode = useMemo(() => () => {
    if (isFromServer) return null;
    const currentPostalCode = actor === null || actor === void 0 ? void 0 : actor.postalCode;
    const currentCountry = actor === null || actor === void 0 ? void 0 : actor.countryOfResidence;
    const previousPostalCode = postalCodeCheck === null || postalCodeCheck === void 0 ? void 0 : postalCodeCheck.code;
    const previousCountry = (postalCodeCheck === null || postalCodeCheck === void 0 ? void 0 : postalCodeCheck.typeInfo) === 'foreign' ? postalCodeCheck.country : 'NOR';
    if (currentPostalCode !== previousPostalCode || currentCountry !== previousCountry) {
      if (!currentPostalCode && (postalCodeCheck === null || postalCodeCheck === void 0 ? void 0 : postalCodeCheck.typeInfo) !== 'invalid') {
        setPostalCodeCheck({
          typeInfo: 'invalid',
          code: currentPostalCode,
          message: 'No postal code'
        });
      } else if (currentPostalCode && currentCountry) {
        return restClient.checkPostalCode({
          code: currentPostalCode,
          country: currentCountry
        }).then(postalCodeUnion => {
          setPostalCodeCheck(() => postalCodeUnion);
        }).catch(reason => {
          setPostalCodeCheck({
            typeInfo: 'invalid',
            code: currentPostalCode,
            message: reason
          });
        });
      }
    }
    return null;
  }, [isFromServer, actor === null || actor === void 0 ? void 0 : actor.postalCode, actor === null || actor === void 0 ? void 0 : actor.countryOfResidence, postalCodeCheck]);
  useEffect(() => {
    checkPostalCode();
    switch (postalCodeCheck === null || postalCodeCheck === void 0 ? void 0 : postalCodeCheck.typeInfo) {
      case 'invalid':
        setValueOfField({
          key: "_".concat(prefix, ".postalPlace"),
          value: ''
        });
        break;
      case 'foreign':
        break;
      case 'valid':
        setValueOfField({
          key: "_".concat(prefix, ".postalPlace"),
          value: postalCodeCheck.area
        });
        break;
      default:
        break;
    }
  }, [postalCodeCheck, checkPostalCode, setValueOfField, prefix]);
  useEffect(() => {
    var _values;
    let newIndex = lastUsedIndex;
    const taxRelationRows = [];
    if (values["_".concat(prefix)] && values["_".concat(prefix)].taxRelations && Object.keys(values["_".concat(prefix)].taxRelations).length > 0 && lastUsedIndex === 0) {
      Object.keys(values["_".concat(prefix)].taxRelations).forEach(() => {
        const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
        const newRow = {
          id,
          index: newIndex
        };
        taxRelationRows.push(newRow);
        newIndex += 1;
      });
      setInternationalTaxRelationRows(taxRelationRows);
      setLastUsedIndex(newIndex);
    }
    if (!((_values = values["_".concat(prefix)]) !== null && _values !== void 0 && _values.taxRelations) && lastUsedIndex === 0 && internationalTaxRelationRows.length === 0) {
      const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
      const newRow = {
        id,
        index: newIndex
      };
      setInternationalTaxRelationRows([newRow]);
    }
  }, [addRow, internationalTaxRelationRows.length, lastUsedIndex, prefix, values]);
  const addPepRow = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedPepIndex + 1;
    setLastUsedPepIndex(newIndex);
    const newRow = {
      id,
      index: newIndex
    };
    setPepRows([...pepRows, newRow]);
  }, [setPepRows, lastUsedPepIndex, pepRows]);
  const onPepRemove = (id, setValueOfField) => {
    const indexToRemove = pepRows.findIndex(row => id === row);
    if (indexToRemove >= 0) {
      const newRows = [...pepRows];
      newRows[indexToRemove].id = DELETED_ROW_ID;
      setPepRows(newRows);
      if (setValueOfField) {
        setValueOfField({
          key: "_".concat(prefix, ".pep._").concat(indexToRemove),
          value: undefined
        });
      }
    }
  };
  const addRcaRow = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedRcaIndex + 1;
    setLastUsedRcaIndex(newIndex);
    const newRow = {
      id,
      index: newIndex
    };
    setRcaRows([...rcaRows, newRow]);
  }, [setRcaRows, lastUsedRcaIndex, rcaRows]);
  const onRcaRemove = (id, setValueOfField) => {
    const indexToRemove = rcaRows.findIndex(row => id === row);
    if (indexToRemove >= 0) {
      const newRows = [...rcaRows];
      newRows[indexToRemove].id = DELETED_ROW_ID;
      setRcaRows(newRows);
      if (setValueOfField) {
        setValueOfField({
          key: "_".concat(prefix, ".rca._").concat(indexToRemove),
          value: undefined
        });
      }
    }
  };
  useEffect(() => {
    var _values2;
    let newPepIndex = lastUsedPepIndex;
    const pepList = [];
    if (values["_".concat(prefix)] && values["_".concat(prefix)].pep && Object.keys(values["_".concat(prefix)].pep).length > 0 && lastUsedPepIndex === 0) {
      Object.keys(values["_".concat(prefix)].pep).forEach(() => {
        const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
        const newRow = {
          id,
          index: newPepIndex
        };
        pepList.push(newRow);
        newPepIndex += 1;
      });
      setPepRows(pepList);
      setLastUsedPepIndex(newPepIndex);
    }
    if (!((_values2 = values["_".concat(prefix)]) !== null && _values2 !== void 0 && _values2.pep) && lastUsedPepIndex === 0 && pepRows.length === 0) {
      const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
      const newRow = {
        id,
        index: newPepIndex
      };
      setPepRows([newRow]);
    }
  }, [lastUsedPepIndex, pepRows.length, prefix, values]);
  useEffect(() => {
    var _values3;
    let newRcaIndex = lastUsedRcaIndex;
    const rcaList = [];
    if (values["_".concat(prefix)] && values["_".concat(prefix)].rca && Object.keys(values["_".concat(prefix)].rca).length > 0 && lastUsedRcaIndex === 0) {
      Object.keys(values["_".concat(prefix)].rca).forEach(() => {
        const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
        const newRow = {
          id,
          index: newRcaIndex
        };
        rcaList.push(newRow);
        newRcaIndex += 1;
      });
      setRcaRows(rcaList);
      setLastUsedRcaIndex(newRcaIndex);
    }
    if (!((_values3 = values["_".concat(prefix)]) !== null && _values3 !== void 0 && _values3.rca) && lastUsedRcaIndex === 0 && rcaRows.length === 0) {
      const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
      const newRow = {
        id,
        index: newRcaIndex
      };
      setRcaRows([newRow]);
    }
  }, [lastUsedRcaIndex, rcaRows.length, prefix, values]);
  const countryOptions = countries === null || countries === void 0 || (_countries$countries = countries.countries) === null || _countries$countries === void 0 ? void 0 : _countries$countries.map((country, i) => {
    return {
      text: country.text,
      value: country.iso3,
      key: (i + 1).toString()
    };
  });
  const rcaRelationTypesOptions = pepAppointmentTypes === null || pepAppointmentTypes === void 0 ? void 0 : pepAppointmentTypes.map(pepRelation => {
    return {
      text: pepRelation.description,
      value: pepRelation.code.toString()
    };
  });
  const pepRelationTypesOptions = pepRelationTypes === null || pepRelationTypes === void 0 ? void 0 : pepRelationTypes.map(pepRelation => {
    return {
      text: pepRelation.description,
      value: pepRelation.code.toString()
    };
  });
  const getRows = (values, setValueOfField) => {
    return internationalTaxRelationRows.filter(row => row.id !== DELETED_ROW_ID).map((row, i) => {
      if (row.id !== DELETED_ROW_ID) {
        var _values4, _values5;
        return /*#__PURE__*/React.createElement("div", {
          key: row.index,
          className: "tax-country-wrapper"
        }, values && (_values4 = values["_".concat(prefix)]) !== null && _values4 !== void 0 && _values4.taxRelations && (_values5 = values["_".concat(prefix)]) !== null && _values5 !== void 0 && (_values5 = _values5.taxRelations["_".concat(i)]) !== null && _values5 !== void 0 && _values5.isFromServer || i === 0 ? undefined : /*#__PURE__*/React.createElement(IconButton, {
          className: "sgw-button remove-icon",
          id: "removeItem",
          onClick: () => {
            onRemove(row, setValueOfField);
          },
          icon: IconTypes.KRYSS_24,
          screenReaderText: "fjern skatterelasjon"
        }), /*#__PURE__*/React.createElement(SelectInput, {
          label: "Land",
          name: "_".concat(prefix, ".taxRelations._").concat(i, ".internationalTaxRelationsCountry"),
          values: [{
            text: 'Velg',
            value: 'velg',
            key: '0'
          }, ...countryOptions],
          validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg'))
        }), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(StandardInputField, {
          label: labels.tin,
          name: "_".concat(prefix, ".taxRelations._").concat(i, ".internationalTin")
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(StandardInputField, {
          label: labels.reasonForMissingTaxNumber,
          name: "_".concat(prefix, ".taxRelations._").concat(i, ".internationalReasonForMissingTaxNumber")
        })));
      }
      return undefined;
    });
  };
  const getPeps = (values, setValueOfField) => {
    return pepRows.map((row, i) => {
      if (row.id !== DELETED_ROW_ID) {
        var _values6, _values7, _values8, _values9;
        return /*#__PURE__*/React.createElement("div", {
          key: row.index,
          className: "pep-wrapper"
        }, (_values6 = values["_".concat(prefix)]) !== null && _values6 !== void 0 && _values6.pep && (_values7 = values["_".concat(prefix)]) !== null && _values7 !== void 0 && (_values7 = _values7.pep["_".concat(i)]) !== null && _values7 !== void 0 && _values7.isFromServer || i === 0 ? undefined : /*#__PURE__*/React.createElement(IconButton, {
          className: "sgw-button remove-icon",
          id: "removeItem",
          onClick: () => {
            onPepRemove(row, setValueOfField);
          },
          icon: IconTypes.KRYSS_24,
          screenReaderText: "fjern pep"
        }), (((_values8 = values["_".concat(prefix)]) === null || _values8 === void 0 ? void 0 : _values8.isBeneficialOwnerPep) === YesNoAnswer.YES || isFromServer && ((_values9 = values["_".concat(prefix)]) === null || _values9 === void 0 || (_values9 = _values9.pep["_".concat(0)]) === null || _values9 === void 0 ? void 0 : _values9.pepCountryCode)) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(SelectInput, {
          label: labels.pepCountryCode,
          name: "_".concat(prefix, ".pep._").concat(i, ".pepCountryCode"),
          values: [{
            text: 'Velg',
            value: 'velg',
            key: '0'
          }, ...countryOptions],
          validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg')),
          disabled: isFromServer && values["_".concat(prefix, ".pep._").concat(i, ".pepCountryCode")] !== undefined
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(SelectInput, {
          label: labels.pepRelationType,
          name: "_".concat(prefix, ".pep._").concat(i, ".pepRelationType"),
          values: [{
            text: 'Velg',
            value: 'velg'
          }, ...pepRelationTypesOptions],
          disabled: isFromServer && values["_".concat(prefix, ".pep._").concat(i, ".pepRelationType")] !== undefined
        }))));
      }
      return undefined;
    });
  };
  const getRcas = (values, setValueOfField) => {
    return rcaRows && rcaRows.map((row, i) => {
      if (values && row.id !== DELETED_ROW_ID) {
        var _values10, _values11, _values12, _values13;
        return /*#__PURE__*/React.createElement("div", {
          key: row.index,
          className: "rca-wrapper"
        }, (_values10 = values["_".concat(prefix)]) !== null && _values10 !== void 0 && _values10.rca && (_values11 = values["_".concat(prefix)]) !== null && _values11 !== void 0 && (_values11 = _values11.rca["_".concat(i)]) !== null && _values11 !== void 0 && _values11.isFromServer || i === 0 ? undefined : /*#__PURE__*/React.createElement(IconButton, {
          className: "sgw-button remove-icon",
          id: "removeItem",
          onClick: () => {
            onRcaRemove(row, setValueOfField);
          },
          icon: IconTypes.KRYSS_24,
          screenReaderText: "fjern rca"
        }), (((_values12 = values["_".concat(prefix)]) === null || _values12 === void 0 ? void 0 : _values12.isBeneficialOwnerRca) === YesNoAnswer.YES || isFromServer && ((_values13 = values["_".concat(prefix)]) === null || _values13 === void 0 || (_values13 = _values13.rca["_".concat(0)]) === null || _values13 === void 0 ? void 0 : _values13.rcaFirstName)) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(StandardInputField, {
          label: "Fornavn",
          name: "_".concat(prefix, ".rca._").concat(i, ".rcaFirstName"),
          validate: requiredField,
          disabled: isFromServer && values["_".concat(prefix, ".rca._").concat(i, ".rcaFirstName")] !== undefined
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(StandardInputField, {
          label: "Etternavn",
          name: "_".concat(prefix, ".rca._").concat(i, ".rcaLastName"),
          validate: requiredField,
          disabled: isFromServer && values["_".concat(prefix, ".rca._").concat(i, ".rcaLastName")] !== undefined
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(SelectInput, {
          label: "Bostedsland",
          name: "_".concat(prefix, ".rca._").concat(i, ".rcaCountryOfResidence"),
          values: [{
            text: 'Velg',
            value: 'velg',
            key: '0'
          }, ...countryOptions],
          validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg')),
          disabled: isFromServer && values["_".concat(prefix, ".rca._").concat(i, ".rcaCountryOfResidence")] !== undefined
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(SelectInput, {
          label: labels.rcaCountryCode,
          name: "_".concat(prefix, ".rca._").concat(i, ".rcaCountryCode"),
          values: [{
            text: 'Velg',
            value: 'velg',
            key: '0'
          }, ...countryOptions],
          validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg')),
          disabled: isFromServer && values["_".concat(prefix, ".rca._").concat(i, ".rcaCountryCode")] !== undefined
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(SelectInput, {
          label: labels.pepRelationType,
          name: "_".concat(prefix, ".rca._").concat(i, ".pepRelationType"),
          values: [{
            text: 'Velg',
            value: 'velg'
          }, ...pepRelationTypesOptions],
          disabled: isFromServer && values["_".concat(prefix, ".rca._").concat(i, ".pepRelationType")] !== undefined
        })), /*#__PURE__*/React.createElement("div", {
          className: "sgw-layout-item"
        }, /*#__PURE__*/React.createElement(SelectInput, {
          label: labels.rcaRelationType,
          name: "_".concat(prefix, ".rca._").concat(i, ".rcaRelationType"),
          values: [{
            text: 'Velg',
            value: 'velg'
          }, ...rcaRelationTypesOptions],
          disabled: isFromServer && values["_".concat(prefix, ".rca._").concat(i, ".rcaRelationType")] !== undefined
        }))));
      }
      return undefined;
    });
  };
  const getInternationalTaxRelations = (values, setValueOfField) => {
    var _values14, _values15, _values16;
    if (((_values14 = values["_".concat(prefix)]) === null || _values14 === void 0 ? void 0 : _values14.hasInternationalTaxRelations) === YesNoAnswer.YES || (_values15 = values["_".concat(prefix)]) !== null && _values15 !== void 0 && _values15.taxRelations && (_values16 = values["_".concat(prefix)]) !== null && _values16 !== void 0 && _values16.taxRelations["_".concat(0)]) {
      return /*#__PURE__*/React.createElement("div", {
        className: "sgw-layout-item",
        id: "internationalTaxRelations"
      }, getRows(values, setValueOfField), /*#__PURE__*/React.createElement(LinkButton, {
        id: "addMore",
        icon: IconTypes.PLUSS_24,
        onClick: addRow
      }, "Legg til"));
    }
    return undefined;
  };
  const getPepRows = (values, setValueOfField) => {
    var _values17, _values18, _values19;
    if (((_values17 = values["_".concat(prefix)]) === null || _values17 === void 0 ? void 0 : _values17.isBeneficialOwnerPep) === YesNoAnswer.YES || (_values18 = values["_".concat(prefix)]) !== null && _values18 !== void 0 && _values18.pep && (_values19 = values["_".concat(prefix)]) !== null && _values19 !== void 0 && (_values19 = _values19.pep["_".concat(0)]) !== null && _values19 !== void 0 && _values19.pepCountryCode) {
      return /*#__PURE__*/React.createElement("div", {
        className: "sgw-layout-item",
        id: "pep"
      }, getPeps(values, setValueOfField), /*#__PURE__*/React.createElement(LinkButton, {
        id: "addMore",
        icon: IconTypes.PLUSS_24,
        onClick: addPepRow
      }, "Legg til"));
    }
    return undefined;
  };
  const getRcaRows = (values, setValueOfField) => {
    var _values20, _values21, _values22;
    if (((_values20 = values["_".concat(prefix)]) === null || _values20 === void 0 ? void 0 : _values20.isBeneficialOwnerRca) === YesNoAnswer.YES || (_values21 = values["_".concat(prefix)]) !== null && _values21 !== void 0 && _values21.rca && (_values22 = values["_".concat(prefix)]) !== null && _values22 !== void 0 && (_values22 = _values22.rca["_".concat(0)]) !== null && _values22 !== void 0 && _values22.rcaFirstName) {
      return /*#__PURE__*/React.createElement("div", {
        className: "sgw-layout-item",
        id: "rca"
      }, getRcas(values, setValueOfField), /*#__PURE__*/React.createElement(LinkButton, {
        id: "addMore",
        icon: IconTypes.PLUSS_24,
        onClick: addRcaRow
      }, "Legg til"));
    }
    return undefined;
  };
  const mappedReasons = reasons && reasons.length > 0 ? reasons
  // Filtered out as it's rather expensive if TE are to remove it...
  .filter(reason => reason.text !== 'Kontroll over stemmeretter').map(el => {
    return {
      text: el.text,
      value: el.code
    };
  }) : [];
  const getCountryOptions = prohibitedCountry => {
    const options = [{
      text: 'Velg',
      value: 'velg',
      key: '0'
    }, ...countryOptions];
    if (prohibitedCountry) {
      return options.filter(country => country.value !== prohibitedCountry);
    }
    return options;
  };
  const selectedOnInternationalTaxRelations = () => {
    var _values23, _values25, _values26;
    if ((_values23 = values["_".concat(prefix)]) !== null && _values23 !== void 0 && _values23.hasInternationalTaxRelations) {
      var _values24;
      return (_values24 = values["_".concat(prefix)]) === null || _values24 === void 0 ? void 0 : _values24.hasInternationalTaxRelations;
    }
    if (isFromServer && (_values25 = values["_".concat(prefix)]) !== null && _values25 !== void 0 && _values25.taxRelations && (_values26 = values["_".concat(prefix)]) !== null && _values26 !== void 0 && (_values26 = _values26.taxRelations["_".concat(0)]) !== null && _values26 !== void 0 && _values26.internationalTaxRelationsCountry) {
      return YesNoAnswer.YES;
    }
    return undefined;
  };
  const selectedOnPep = () => {
    var _values27, _values29, _values30;
    if ((_values27 = values["_".concat(prefix)]) !== null && _values27 !== void 0 && _values27.isBeneficialOwnerPep) {
      var _values28;
      return (_values28 = values["_".concat(prefix)]) === null || _values28 === void 0 ? void 0 : _values28.isBeneficialOwnerPep;
    }
    if (isFromServer && (_values29 = values["_".concat(prefix)]) !== null && _values29 !== void 0 && _values29.pep && (_values30 = values["_".concat(prefix)]) !== null && _values30 !== void 0 && (_values30 = _values30.pep["_".concat(0)]) !== null && _values30 !== void 0 && _values30.pepCountryCode) {
      return YesNoAnswer.YES;
    }
    return undefined;
  };
  const selectedOnRca = () => {
    var _values31, _values33, _values34;
    if ((_values31 = values["_".concat(prefix)]) !== null && _values31 !== void 0 && _values31.isBeneficialOwnerRca) {
      var _values32;
      return (_values32 = values["_".concat(prefix)]) === null || _values32 === void 0 ? void 0 : _values32.isBeneficialOwnerRca;
    }
    if (isFromServer && (_values33 = values["_".concat(prefix)]) !== null && _values33 !== void 0 && _values33.rca && (_values34 = values["_".concat(prefix)]) !== null && _values34 !== void 0 && (_values34 = _values34.rca["_".concat(0)]) !== null && _values34 !== void 0 && _values34.rcaFirstName) {
      return YesNoAnswer.YES;
    }
    return undefined;
  };
  const getError = meta => {
    if (meta.touched && meta.error && typeof meta.error === 'string') {
      return /*#__PURE__*/React.createElement("div", {
        className: "medium-margin-top"
      }, /*#__PURE__*/React.createElement(ErrorMessage, {
        text: meta.error
      }));
    }
    return '';
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "actor-section-wrapper",
    key: prefix
  }, !isFromServer && prefix !== '0' && /*#__PURE__*/React.createElement(IconButton, {
    className: "sgw-button remove-icon",
    id: "removeItem",
    onClick: onRemoveRow,
    icon: IconTypes.KRYSS_24,
    screenReaderText: "fjern dette elementet fra skjema"
  }), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "Fornavn",
    name: "_".concat(prefix, ".firstName"),
    validate: requiredField,
    disabled: isFromServer && ((_values35 = values["_".concat(prefix)]) === null || _values35 === void 0 ? void 0 : _values35.firstName) !== undefined
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "Etternavn",
    name: "_".concat(prefix, ".lastName"),
    validate: requiredField,
    disabled: isFromServer && ((_values36 = values["_".concat(prefix)]) === null || _values36 === void 0 ? void 0 : _values36.lastName) !== undefined
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "F\xF8dselsnummer / D-nummer",
    name: "_".concat(prefix, ".publicId"),
    validate: composeValidators(requiredField, isValidSSNFactory(isFromServer)),
    disabled: isFromServer && ((_values37 = values["_".concat(prefix)]) === null || _values37 === void 0 ? void 0 : _values37.publicId) !== undefined
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(SelectInput, {
    label: "F\xF8deland",
    name: "_".concat(prefix, ".birthCountry"),
    disabled: isFromServer && ((_values38 = values["_".concat(prefix)]) === null || _values38 === void 0 ? void 0 : _values38.birthCountry) !== undefined,
    values: [{
      text: 'Velg',
      value: 'velg',
      key: '0'
    }, ...countryOptions],
    validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg'))
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(SelectInput, {
    label: "Statsborgerskap",
    name: "_".concat(prefix, ".citizenship"),
    disabled: isFromServer && ((_values39 = values["_".concat(prefix)]) === null || _values39 === void 0 ? void 0 : _values39.citizenship) !== undefined,
    values: getCountryOptions((_values40 = values["_".concat(prefix)]) === null || _values40 === void 0 ? void 0 : _values40.secondaryCitizenship),
    validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg'))
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(SelectInput, {
    label: "Sekund\xE6rt statsborgerskap",
    disabled: isFromServer && ((_values41 = values["_".concat(prefix)]) === null || _values41 === void 0 ? void 0 : _values41.secondaryCitizenship) !== undefined,
    name: "_".concat(prefix, ".secondaryCitizenship"),
    values: getCountryOptions((_values42 = values["_".concat(prefix)]) === null || _values42 === void 0 ? void 0 : _values42.citizenship)
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(SelectInput, {
    label: "Bostedsland",
    name: "_".concat(prefix, ".countryOfResidence"),
    values: [{
      text: 'Velg',
      value: 'velg',
      key: '0'
    }, ...countryOptions],
    disabled: isFromServer && ((_values43 = values["_".concat(prefix)]) === null || _values43 === void 0 ? void 0 : _values43.countryOfResidence) !== undefined,
    validate: composeValidators(requiredField, value => valueNotAllowed(value, 'velg', 'Velg'))
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "Postnummer",
    name: "_".concat(prefix, ".postalCode"),
    validate: composeValidators(requiredField, () => (postalCodeCheck === null || postalCodeCheck === void 0 ? void 0 : postalCodeCheck.typeInfo) === 'invalid' && postalCodeCheck.message || ''),
    disabled: isFromServer && ((_values44 = values["_".concat(prefix)]) === null || _values44 === void 0 ? void 0 : _values44.postalCode) !== undefined
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "Poststed",
    name: "_".concat(prefix, ".postalPlace"),
    validate: requiredField,
    disabled: isFromServer && ((_values45 = values["_".concat(prefix)]) === null || _values45 === void 0 ? void 0 : _values45.postalPlace) !== undefined
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "Adresse",
    name: "_".concat(prefix, ".address"),
    validate: requiredField,
    disabled: isFromServer && ((_values46 = values["_".concat(prefix)]) === null || _values46 === void 0 ? void 0 : _values46.address) !== undefined
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: "Eierandel",
    type: "number",
    name: "_".concat(prefix, ".eierandel"),
    validate: composeValidators(requiredField, positiveNumbersOnly, value => maxValueOfNumber(value, 100, "Eierandel kan ikke v\xE6re mer enn 100%")),
    disabled: isFromServer && ((_values47 = values["_".concat(prefix)]) === null || _values47 === void 0 ? void 0 : _values47.eierandel) !== undefined
  }))), /*#__PURE__*/React.createElement(Field, {
    name: "_".concat(prefix, ".beneficialOwnerReasons"),
    validate: requiresAtLeastOneBeneficialOwnerCheckboxSelected,
    render: _ref2 => {
      let {
        meta
      } = _ref2;
      return /*#__PURE__*/React.createElement("div", {
        className: "form-control"
      }, /*#__PURE__*/React.createElement("label", {
        className: "sgw-select__label"
      }, /*#__PURE__*/React.createElement("div", {
        className: "sgw-select__mainlabel sgw-select__mainlabel_with_help_icon"
      }, labels.pepAppointmentType, " ", /*#__PURE__*/React.createElement(BeneficialOwnersHelpModal, null))), mappedReasons.map(el => {
        return /*#__PURE__*/React.createElement("div", {
          className: "checkbox-field",
          key: "".concat(prefix, "-").concat(el.value)
        }, /*#__PURE__*/React.createElement(CheckboxField, {
          name: "_".concat(prefix, ".beneficialOwnerReasons.").concat(el.value),
          label: el.text,
          value: el.value
        }));
      }), getError(meta));
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "pep-section form-control"
  }, /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
    selected: selectedOnPep(),
    label: labels.isPep,
    name: "_".concat(prefix, ".isBeneficialOwnerPep"),
    validate: requiredField
  }), getPepRows(values, setValueOfField)), /*#__PURE__*/React.createElement("div", {
    className: "rca-section form-control"
  }, /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
    selected: selectedOnRca(),
    label: labels.isRca,
    name: "_".concat(prefix, ".isBeneficialOwnerRca"),
    validate: requiredField
  }), getRcaRows(values, setValueOfField)), /*#__PURE__*/React.createElement("div", {
    className: "us-tax-section"
  }, /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
    selected: (_values48 = values["_".concat(prefix)]) === null || _values48 === void 0 ? void 0 : _values48.hasAmericanTaxRelation,
    label: labels.hasAmericanTaxRelation,
    name: "_".concat(prefix, ".hasAmericanTaxRelation"),
    validate: requiredField
  }), ((_values49 = values["_".concat(prefix)]) === null || _values49 === void 0 ? void 0 : _values49.hasAmericanTaxRelation) === YesNoAnswer.YES && /*#__PURE__*/React.createElement("div", {
    className: "tax-country-wrapper"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: labels.tin,
    name: "_".concat(prefix, ".tin"),
    validate: values["_".concat(prefix)].reasonForMissingTaxNumber ? optional : requiredField
  })), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item"
  }, /*#__PURE__*/React.createElement(StandardInputField, {
    label: labels.reasonForMissingTaxNumber,
    name: "_".concat(prefix, ".reasonForMissingTaxNumber"),
    validate: values["_".concat(prefix)].tin ? optional : requiredField
  })))), /*#__PURE__*/React.createElement("div", {
    className: "sgw-layout-item tax-section"
  }, /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
    selected: selectedOnInternationalTaxRelations(),
    label: labels.hasInternationalTaxRelations,
    name: "_".concat(prefix, ".hasInternationalTaxRelations"),
    validate: requiredField
  }), getInternationalTaxRelations(values, setValueOfField)));
};
export default CorporateBeneficialOwnerActorItem;